// Copied from functions in Tfs\Service\WebAccess\WorkItemTracking\Scripts\OM\QueryAdapter.ts
/// <amd-module name="Wit/Common/QueryUtils/WiqlGenerator" />
import { equals, format, localeIgnoreCaseComparer, startsWith } from "VSS/Core/Util/String";
import { CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import * as Resources from "Wit/Common/QueryUtils/Resources";
import {
    getInvariantOperator,
    getLocalizedOperator,
    isFieldComparisonOperator,
    isNonNullableField,
    isOrOperator,
    isProjectMacro,
    isValidMacro,
    quoteString,
    toInvariantDateString,
    WiqlOperators
} from "Wit/Common/WiqlOperators";
import { parseCurrentIteration, parseDateMacro, parseTeamAreas } from "Wit/Common/WiqlValues";
import {
    IClause,
    IEditInfo,
    IFilter,
    IFilterGroup,
    IQuerySortColumn,
    IWorkItemLinkType,
    IWorkItemLinkTypeEnd
} from "Wit/QueriesHub/Common/Interfaces";
import { LinkQueryMode } from "./QueryTypeConstants";

namespace Exceptions {
    export const LinkTypeEndDoesNotExistException = "VSS.WorkItemTracking.LinkTypeEndDoesNotExistException";
    export const InvalidQuerySyntaxException = "VSS.WorkItemTracking.InvalidQuerySyntaxException";
    export const InvalidQueryFilterRowException = "VSS.WorkItemTracking.InvalidQueryFilterRowException";
    export const FieldDoesNotExistException = "VSS.WorkItemTracking.FieldDoesNotExistException";
}

function createError(name: string, message: string): Error {
    const err = new Error(message);
    err.name = name;
    return err;
}

function Error_invalidQuerySyntax(details: any, node: any): Error {
    return createError(Exceptions.InvalidQuerySyntaxException, format(Resources.InvalidQuerySyntax, details, node));
}

function Error_invalidQueryFilterRow(message: any, rowIndex: number): Error {
    return createError(Exceptions.InvalidQueryFilterRowException, format(message, rowIndex));
}

function Error_linkTypeEndDoesNotExist(linkTypeEnd: number | string): Error {
    return createError(Exceptions.LinkTypeEndDoesNotExistException, format(Resources.WorkItemLinkTypeEndDoesNotExist, linkTypeEnd));
}

function Error_fieldNotFound(localizedFieldName: string): Error {
    return createError(Exceptions.FieldDoesNotExistException, format(Resources.FieldNotFoundError, localizedFieldName));
}

export namespace WiqlGenerator {
    let fields: IFieldEntry[];
    const linkTypeEnds: IWorkItemLinkTypeEnd[] = [];
    const intRegex = /^-?[0-9]+?$/;
    const trueRegex = /^true$/i;
    const falseRegex = /^false$/i;
    const positiveIntRegex = /^[0-9]+?$/;
    const anySyntaxRegex1 = /[\s'"()]+$/;
    const anySyntaxRegex2 = /^[\s'"()]+/;
    const idListRegex = /\"([^\"]*)\"/i;
    let _isDeletedField: IFieldEntry | undefined;

    export function generateWiql(
        fieldList: IFieldEntry[],
        editInfo: IEditInfo,
        columns: string[],
        sortColumns: IQuerySortColumn[],
        linkTypes: IWorkItemLinkType[],
        isDeletedField?: IFieldEntry
    ): string {
        const wiql: string[] = [];
        _isDeletedField = isDeletedField;
        let filter: string;
        let orderBy: string;
        let mode: string;
        fields = fieldList;
        linkTypes.forEach(link => {
            if (!linkTypeEnds.find(end => end.id === link.forwardEnd.id)) {
                linkTypeEnds.push(link.forwardEnd);
            }
            if (!linkTypeEnds.find(end => end.id === link.reverseEnd.id)) {
                linkTypeEnds.push(link.reverseEnd);
            }
        });
        wiql.push(generateSelectClause(columns));

        if (editInfo.mode <= LinkQueryMode.WorkItems) {
            wiql.push("FROM WorkItems");
        } else {
            wiql.push("FROM WorkItemLinks");
        }

        filter = generateFilter(editInfo).trim();
        if (filter) {
            wiql.push("WHERE " + filter);
        }

        orderBy = generateOrderByClause(sortColumns, LinkQueryMode.isLinkQuery(editInfo.mode));
        if (orderBy) {
            wiql.push(orderBy);
        }

        mode = getModeClause(editInfo.mode);
        if (mode) {
            wiql.push(mode);
        }

        return wiql.join(" ");
    }

    export function generateSelectClause(columns: string[]): string {
        if (columns.length > 0) {
            return (
                "SELECT " +
                columns
                    .map(column => {
                        return "[" + column + "]";
                    })
                    .join(",")
            );
        } else {
            return "SELECT [System.Id]";
        }
    }

    function generateFilter(editInfo: IEditInfo): string {
        if (LinkQueryMode.isLinkQuery(editInfo.mode)) {
            const isTree = LinkQueryMode.isTreeQuery(editInfo.mode);
            const targetFilter = isTree ? editInfo.treeTargetFilter : editInfo.linkTargetFilter;
            const linkTypes = isTree ? editInfo.treeLinkTypes : editInfo.linkTypes;
            const whereClause = [];
            let filterText: string | undefined = undefined;

            filterText = generateFilterExpression(editInfo.sourceFilter, "Source", editInfo.teamProject).trim();
            if (filterText) {
                whereClause.push("(" + filterText + ")");
            }

            filterText = generateLinkFilter(linkTypes).trim();
            if (filterText) {
                whereClause.push("(" + filterText + ")");
            }

            filterText = generateFilterExpression(targetFilter, "Target", editInfo.teamProject).trim();
            if (filterText) {
                whereClause.push("(" + filterText + ")");
            }

            return whereClause.join(" AND ");
        } else {
            return generateFilterExpression(editInfo.sourceFilter, "", editInfo.teamProject).trim();
        }
    }

    function generateFilterExpression(filter: IFilter, prefix: string, teamProject: string): string {
        const clauses: IClause[] = [];
        const filterString: string[] = [];
        let groups = filter.groups;
        let index: number;
        let addParen = false;

        if (filter.clauses) {
            // clone groups first
            groups = groups && [...groups];

            index = 0;

            filter.clauses.forEach(clause => {
                const fieldName = clause.fieldName.trim();

                if (fieldName) {
                    const operator = clause.operator.trim();
                    if (!operator) {
                        throw Error_invalidQueryFilterRow(Resources.QueryFilterErrorMissingOperator, clause.index + 1);
                    }

                    if (!clause.value.trim()) {
                        if (
                            isFieldComparisonOperator(operator) ||
                            equals(WiqlOperators.OperatorIn, getInvariantOperator(operator), true /* ignorecase */) ||
                            equals(WiqlOperators.OperatorNotIn, getInvariantOperator(operator), true /* ignorecase */) ||
                            getFieldType(fieldName) === FieldType.Boolean
                        ) {
                            throw Error_invalidQueryFilterRow(Resources.QueryFilterErrorEmptyValue, clause.index + 1);
                        }
                    }
                    clauses.push({ ...clause, ...{ index: index, originalIndex: clause.index } });
                    index++;
                } else if (groups && groups.length) {
                    groups = updateFilterGroups(groups, index + 1, false);
                }
            });
        }
        // basic logic should be:
        //  If 'teamProject' is NOT empty and NOT '[Any]' then
        //      If there are only 'ANDs' at the top level add the team project clause to the filter string (don't forget the prefix)
        //      else Add the team project clause and add an open paren, add a close paren at the end in this case.
        //  Else do nothing
        if (teamProject) {
            if (!isProjectInTopLevelAndAllAnds(filter, teamProject)) {
                let teamProjectClause = "";
                let teamProjectName: string;

                if (isProjectMacro(teamProject, false)) {
                    teamProjectName = teamProject;
                } else {
                    teamProjectName = quoteString(teamProject);
                }

                if (prefix) {
                    teamProjectClause = "[" + prefix + "].";
                }

                teamProjectClause += "[" + CoreFieldRefNames.TeamProject + "] = " + teamProjectName;

                if (clauses.length > 0) {
                    teamProjectClause += " AND";

                    if (!isTopLevelAndsOnly(filter)) {
                        addParen = true;
                        teamProjectClause += " (";
                    }
                }

                filterString.push(teamProjectClause);
            }
        }

        clauses.forEach((clause, i) => {
            const clauseString: string[] = [];
            let parens = "";
            let invariantValue: string;

            if (i > 0) {
                clauseString.push(getInvariantOperator(clause.logicalOperator));
            }

            if (groups && groups.length) {
                groups.forEach(g => {
                    if (g.start === clause.index + 1) {
                        parens += "(";
                    }
                });

                if (parens) {
                    clauseString.push(parens);
                }
            }

            clause = processClause(clause);

            const localizedFieldName = clause.fieldName;
            const localizedOperator = clause.operator;
            const localizedValue = clause.value;

            const invariantFieldName = getInvariantFieldName(localizedFieldName, true); // will throw syntax exception

            const invariantOperator = getInvariantOperator(localizedOperator);

            if (isFieldComparisonOperator(localizedOperator)) {
                const rightFieldName = getInvariantFieldName(localizedValue, true); // will throw syntax exception

                if (prefix) {
                    invariantValue = "[" + prefix + "].[" + rightFieldName + "]";
                } else {
                    invariantValue = "[" + rightFieldName + "]";
                }
            } else if (invariantOperator === WiqlOperators.OperatorIsEmpty || invariantOperator === WiqlOperators.OperatorIsNotEmpty) {
                invariantValue = "";
            } else {
                invariantValue = getInvariantFieldValue(invariantFieldName, invariantOperator, localizedValue);
            }

            if (prefix) {
                clauseString.push("[" + prefix + "].[" + invariantFieldName + "]");
            } else {
                clauseString.push("[" + invariantFieldName + "]");
            }

            clauseString.push(invariantOperator);
            clauseString.push(invariantValue);

            if (groups && groups.length) {
                parens = "";
                groups.forEach(g => {
                    if (g.end === clause.index + 1) {
                        parens += ")";
                    }
                });

                if (parens) {
                    clauseString.push(parens);
                }
            }

            filterString.push(clauseString.join(" "));
        });

        let retval = filterString.join(" ");

        if (addParen) {
            retval = retval + ")";
        }

        return retval;
    }

    function getFieldType(fieldName: string): FieldType {
        return fields.find(field => field.referenceName === fieldName)?.type ?? FieldType.String;
    }

    function getField(fieldName: string): IFieldEntry {
        return fields.find(field => field.referenceName !== CoreFieldRefNames.Parent && field.referenceName === fieldName) ?? ({} as IFieldEntry);
    }

    function getInvariantFieldName(localizedFieldName: string, throwError: boolean): string {
        const field = fields.find(field => field.name === localizedFieldName);
        if (field) {
            return field.referenceName;
        } else if (_isDeletedField && localizedFieldName === _isDeletedField.name) {
            return _isDeletedField.referenceName;
        } else if (throwError) {
            throw Error_fieldNotFound(localizedFieldName);
        } else {
            return localizedFieldName;
        }
    }

    // Copied from Vssf/WebPlatform/Platform/Scripts/VSS/Utils/UI.ts
    function updateFilterGroups(groups: IFilterGroup[], clauseNumber: number, insert: boolean): IFilterGroup[] {
        let newGroups: IFilterGroup[];
        let gmap: { [key: string]: boolean };

        if (groups && groups.length) {
            if (insert) {
                groups.forEach(g => {
                    if (g.end >= clauseNumber) {
                        if (g.start >= clauseNumber) {
                            g.start++;
                        }
                        g.end++;
                    }
                });
            } else {
                newGroups = [];
                gmap = {};
                groups.forEach(g => {
                    let gkey: string;
                    if (g.end >= clauseNumber) {
                        if (g.start > clauseNumber) {
                            g.start = Math.max(1, g.start - 1);
                        }
                        g.end = Math.max(1, g.end - 1);
                    }

                    if (g.start !== g.end) {
                        gkey = g.start + "_" + g.end;

                        if (!(gkey in gmap)) {
                            gmap[gkey] = true;
                            newGroups.push(g);
                        }
                    }
                });
                return newGroups;
            }
        }
        return groups;
    }

    function isProjectInTopLevelAndAllAnds(filter: IFilter, teamProject: string): boolean {
        if (!filter || !filter.clauses) {
            return false;
        }

        let retval = false;
        filter.clauses.forEach(clause => {
            const logicalOperator = getInvariantOperator(clause.logicalOperator);

            if (isOrOperator(logicalOperator, false) && doesLogicalOperatorApplyToTopLevelOfQuery(clause, filter.groups)) {
                retval = false;
                return false;
            } else if (
                !isOrOperator(logicalOperator, false) &&
                doesLogicalOperatorApplyToTopLevelOfQuery(clause, filter.groups) &&
                getInvariantOperator(clause.operator) === WiqlOperators.OperatorEqualTo &&
                getInvariantFieldName(clause.fieldName, false) === CoreFieldRefNames.TeamProject &&
                (isProjectMacro(clause.value, true) || localeIgnoreCaseComparer(clause.value, teamProject) === 0)
            ) {
                retval = true;
            }
        });

        return retval;
    }

    function doesLogicalOperatorApplyToTopLevelOfQuery(clause: IClause, groups: IFilterGroup[]): boolean {
        // The filter grid control changes the indexes (enumerates over them
        // and sets them to a linear set of values which is 0-based instead of 1-based.
        // TODO:  We should fix this, it should not be changing the datasource in this way.
        const clauseIndex = clause.index + 1;
        for (const group of groups) {
            if (clauseIndex > group.start && clauseIndex <= group.end) {
                return false;
            }
        }
        return true;
    }

    function isTopLevelAndsOnly(filter: IFilter): boolean {
        if (!filter || !filter.clauses) {
            return true;
        }

        for (const clause of filter.clauses) {
            const logicalOperator = getInvariantOperator(clause.logicalOperator);

            if (isOrOperator(logicalOperator, false) && doesLogicalOperatorApplyToTopLevelOfQuery(clause, filter.groups)) {
                return false;
            }
        }

        return true;
    }

    function processClause(clause: IClause): IClause {
        if (fieldSupportsAnySyntax(clause.fieldName)) {
            return convertFromSpecialAnySyntax(clause);
        }

        return clause;
    }

    function fieldSupportsAnySyntax(fieldName: string): boolean {
        const field = fields.find(field => field.name === fieldName);
        return field !== undefined && isNonNullableField(field.referenceName);
    }

    function convertFromSpecialAnySyntax(clause: IClause): IClause {
        let value: string;
        if (clause.value) {
            value = clause.value.replace(anySyntaxRegex1, "").replace(anySyntaxRegex2, "");

            if (
                localeIgnoreCaseComparer(value, Resources.AnyOptionText) === 0 &&
                getInvariantOperator(clause.operator) === WiqlOperators.OperatorEqualTo
            ) {
                return { ...clause, ...{ operator: getLocalizedOperator(WiqlOperators.OperatorNotEqualTo), value: "" } };
            }
        }

        return clause;
    }

    function findLinkTypeEnd(idOrName: number | string): IWorkItemLinkTypeEnd {
        const search = ("" + idOrName).toUpperCase();

        for (const linkTypeEnd of linkTypeEnds) {
            if (linkTypeEnd.id === idOrName) {
                return linkTypeEnd;
            } else if (linkTypeEnd.name.toUpperCase() === search) {
                return linkTypeEnd;
            } else if (linkTypeEnd.immutableName.toUpperCase() === search) {
                return linkTypeEnd;
            }
        }

        throw Error_linkTypeEndDoesNotExist(idOrName);
    }

    function getInvariantFieldValue(invariantFieldName: string, invariantOperator: string, localizedValue: string): string {
        let isEmpty: boolean;
        let values: string[];
        let fieldType: number;
        let isMacro: boolean;
        let isDateTime: boolean;
        let isInteger: boolean;
        let isNumber: boolean;
        let isBoolean: boolean;
        let linkTypeEnd: IWorkItemLinkTypeEnd;
        let num: number;
        let invariantValue = "";
        let localDate: Date | undefined;
        let year: number;

        if (localizedValue !== null && localizedValue !== undefined) {
            // if value is null or undefined we will return
            localizedValue = localizedValue.trim();
            isEmpty = localizedValue ? false : true;

            if (invariantOperator === WiqlOperators.OperatorIn || invariantOperator === WiqlOperators.OperatorNotIn) {
                values = [];
                if (getField(invariantFieldName).isIdentity) {
                    values = parseIdentityListString(localizedValue);
                } else {
                    values = localizedValue.split(",").filter(v => !!v);
                }
                values = values.map(v => getInvariantFieldValue(invariantFieldName, "", v));
                invariantValue = "(" + values.join(",") + ")";
            } else if (equals(invariantFieldName, "System.LinkType", true /* ignorecase */)) {
                try {
                    linkTypeEnd = findLinkTypeEnd(localizedValue);
                } catch (e) {
                    throw Error_invalidQuerySyntax(e.message, localizedValue);
                }
                invariantValue = quoteString(linkTypeEnd.immutableName);
            } else if (isEmpty) {
                invariantValue = "''";
            } else {
                fieldType = getFieldType(invariantFieldName);

                isMacro = startsWith(localizedValue, WiqlOperators.MacroStart);

                if (isMacro) {
                    const currentIterationParts = parseCurrentIteration(localizedValue);
                    if (currentIterationParts) {
                        if (!currentIterationParts.team) {
                            throw Error_invalidQuerySyntax(Resources.ErrorExpectingTeam, localizedValue);
                        } else {
                            const { offset, team } = currentIterationParts;
                            const offsetStr = offset > 0 ? ` + ${offset}` : offset < 0 ? ` - ${-offset}` : "";
                            const teamStr = team ? `('${team}')` : "";
                            return `${WiqlOperators.MacroCurrentIteration}${teamStr}${offsetStr}`;
                        }
                    }

                    const teamAreaParts = parseTeamAreas(localizedValue);
                    if (teamAreaParts) {
                        if (!teamAreaParts.team) {
                            throw Error_invalidQuerySyntax(Resources.ErrorExpectingTeam, localizedValue);
                        } else {
                            const { team } = teamAreaParts;
                            const teamStr = team ? `('${team}')` : "";
                            return `${WiqlOperators.MacroTeamAreas}${teamStr}`;
                        }
                    }

                    const dateMacroParts = parseDateMacro(localizedValue);
                    if (dateMacroParts) {
                        if (!dateMacroParts.invariantMacro) {
                            throw Error_invalidQuerySyntax(Resources.ErrorExpectingTeam, localizedValue);
                        } else {
                            const { invariantMacro, modifier, offset } = dateMacroParts;
                            let macroStr = `${invariantMacro}`;

                            if (modifier) {
                                macroStr += `('${modifier}')`;
                            }

                            if (offset) {
                                macroStr += `${offset}`;
                            }
                            return macroStr;
                        }
                    }

                    if (isValidMacro(localizedValue, true)) {
                        invariantValue = getInvariantOperator(localizedValue);
                    } else {
                        throw Error_invalidQuerySyntax(format(Resources.QueryFilterInvalidMacro, localizedValue), localizedValue);
                    }
                } else {
                    isDateTime = fieldType === FieldType.DateTime;
                    isInteger = fieldType === FieldType.Integer;
                    isNumber = fieldType === FieldType.Double;
                    isBoolean = fieldType === FieldType.Boolean;

                    if (isDateTime) {
                        localDate = new Date(Date.parse(localizedValue));
                        if (localDate === undefined || Number.isNaN(localDate as any)) {
                            throw Error_invalidQuerySyntax(Resources.ErrorExpectingDateTime, localizedValue);
                        }

                        year = localDate.getFullYear();

                        if (year < 1753 || year > 9999) {
                            throw Error_invalidQuerySyntax(Resources.ErrorDateTimeValueOutOfRange, localizedValue);
                        }

                        invariantValue = quoteString(toInvariantDateString(localDate));
                    } else if (isInteger) {
                        if (!intRegex.test(localizedValue)) {
                            throw Error_invalidQuerySyntax(Resources.ErrorExpectingIntegerValue, localizedValue);
                        }

                        invariantValue = localizedValue;
                    } else if (isNumber) {
                        num = Number(localizedValue);
                        if (Number.isNaN(num)) {
                            throw Error_invalidQuerySyntax(Resources.ErrorExpectingNumericValue, localizedValue);
                        } else {
                            invariantValue = localizedValue.toString();
                        }
                    } else if (isBoolean) {
                        if (trueRegex.test(localizedValue)) {
                            invariantValue = "true";
                        } else if (falseRegex.test(localizedValue)) {
                            invariantValue = "false";
                        } else if (positiveIntRegex.test(localizedValue)) {
                            invariantValue = parseInt(localizedValue, 10) ? "true" : "false";
                        } else {
                            throw Error_invalidQuerySyntax(Resources.ErrorExpectingBooleanValue, localizedValue);
                        }
                    } else {
                        invariantValue = quoteString(localizedValue);
                    }
                }
            }
        }

        return invariantValue;
    }

    function parseIdentityListString(listString: string): string[] {
        if (listString === "") {
            return [];
        }
        // Look for the first non double quote characters group surrounded by double quotes
        const match: RegExpMatchArray | null = listString.match(idListRegex);
        if (match === null) {
            return listString.split(",");
        } else if (match && match.index) {
            // If there is a match, extract identity and parse the remaining
            return parseIdentityListString(listString.substr(0, match.index - 1))
                .concat([match[1]])
                .concat(parseIdentityListString(listString.substr(match.index + match[0].length + 1)));
        } else {
            return []; // should never occur
        }
    }

    function generateLinkFilter(linkTypes: string): string {
        let linkTypeEndNames: string[] = [];

        if (linkTypes) {
            for (let v of linkTypes.split(",")) {
                let linkTypeEnd: IWorkItemLinkTypeEnd;
                v = v.trim();
                if (localeIgnoreCaseComparer(v, Resources.AnyOptionText) === 0) {
                    linkTypeEndNames = ["[System.Links.LinkType] <> ''"];
                    break;
                } else {
                    try {
                        linkTypeEnd = findLinkTypeEnd(v);
                        linkTypeEndNames.push("[System.Links.LinkType] = " + quoteString(linkTypeEnd.immutableName));
                    } catch (e) {
                        throw Error_invalidQuerySyntax(e.message, v);
                    }
                }
            }
        }

        return linkTypeEndNames.join(" OR ");
    }

    export function generateOrderByClause(sortColumns: IQuerySortColumn[], isLinkQuery: boolean): string {
        let sc = sortColumns;

        if (!isLinkQuery) {
            if (sortColumns && sortColumns.length) {
                sc = [];
                sortColumns.forEach(column => {
                    if (column.name && !equals(column.name, "System.Links.LinkType", true /* ignorecase */)) {
                        sc.push(column);
                    }
                });
            }
        }

        if (sc && sc.length) {
            return (
                "ORDER BY " +
                sc
                    .map(column => {
                        return "[" + column.name + "]" + (column.descending ? " DESC" : "");
                    })
                    .join(",")
            );
        }

        return "";
    }

    function getModeClause(mode: number): string {
        switch (mode) {
            case LinkQueryMode.LinksMustContain:
                return "mode(MustContain)";
            case LinkQueryMode.LinksMayContain:
                return "mode(MayContain)";
            case LinkQueryMode.LinksDoesNotContain:
                return "mode(DoesNotContain)";
            case LinkQueryMode.LinksRecursive:
                return "mode(Recursive)";
            case LinkQueryMode.LinksRecursiveReturnMatchingChildren:
                return "mode(Recursive, ReturnMatchingChildren)";
        }

        return "";
    }
}
